import React, {useEffect, useState} from "react";
import {handleError} from "../../helpers/helpers";
import {
    cancelPaymentIntent,
    cancelSetupIntent,
    createCustomerForAccount,
    createSetupIntent,
    loadSettings
} from "../../api/requests";
import {ServerResponse, Settings} from "../../models/models";
import {PAYMENT_METHOD_PAGES, PAYMENT_TYPE, RESPONSE_TYPES} from "../../constants";
import Loading from "../common/Loading";
import ResponseCard from "../common/cards/ResponseCard";
import useBeforeUnload from "../../helpers/useBeforeUnload";
import PaymentMethodSplashPage from "./pages/PaymentMethodSplashPage";
import {Elements} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import PaymentMethodAddCardPage from "./pages/PaymentMethodAddCardPage";
import PaymentMethodHistoryPage from "./pages/PaymentMethodHistoryPage";
import PaymentMethodsDataRefreshManager from "./PaymentMethodsDataRefreshManager";

const PaymentMethodsContainer = () => {
    const [serverResponse, setServerResponse] = useState(new ServerResponse());
    const [loading, setLoading] = useState(true);
    const [appCenterData, setAppCenterData] = useState(null);
    const [settings, setSettings] = useState(null);

    const {customerId, allPaymentMethods, product} = appCenterData || {};
    const {publishableKey} = settings || {};

    const [page, setPage] = useState(PAYMENT_METHOD_PAGES.SPLASH);
    const [selectedPlan] = useState(null);
    const [clientSecretKey, setClientSecretKey] = useState(""); // Unique checkout session key for customer.
    const [payNow, setPayNow] = useState(null);

    const [shouldRefresh, setShouldRefresh] = useState(false);

    // A PaymentIntent is used to keep track of the payment flow and is created upon
    // React app load: https://stripe.com/docs/payments/payment-intents
    const [paymentIntentId, setPaymentIntentId] = useState("");

    useEffect(() => {
        if (page === PAYMENT_METHOD_PAGES.ADD_NEW) {
            if (!publishableKey) {
                console.error('Failed to load checkout form.');
                handleError(i18n.payments.error.genericTitle, i18n.payments.error.failedToLoadCheckoutForm, setServerResponse);
                return;
            } else if (!customerId) {
                createCustomer(() => {
                    createIntent();
                });
                return;
            }

            createIntent();
        }
    }, [page]);

    const createCustomer = (callback) => {
        setLoading(true);
        createCustomerForAccount((result) => {
            setLoading(false);
            if (result.status !== 200) {
                console.error('Failed to create customer account');
                handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                return;
            }

            setAppCenterData({
                ...appCenterData,
                customerId: result.data.customerId,
            });

            if (callback) {
                callback();
            }
        });
    };

    const createIntent = () => {
        setLoading(true);
        setPaymentIntentId("");

        createSetupIntent(product.id, PAYMENT_TYPE.AC, (result) => {
            setLoading(false);
            if (result.status !== 200) {
                console.error('Failed to create setup intent');
                handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                return;
            }
            setPaymentIntentId(result.data.id);
            setClientSecretKey(result.data.clientSecret);
        });
    };

    // Clean Up //////////////////////////////////////////////////////////////
    const handlePreventDefault = (event) => {
        event.preventDefault(); // Cancel the event as stated by the standard.
        event.returnValue = ''; // Chrome requires returnValue to be set.
    }

    const handleLeavingPage = (event) => {
        // Cancel any payment attempts that have not yet been processed.
        if (page === PAYMENT_METHOD_PAGES.ADD_NEW && paymentIntentId) {
            handlePreventDefault(event);

            if (paymentIntentId.includes('seti')) {
                cancelSetupIntent(paymentIntentId, () => {
                });
            } else {
                cancelPaymentIntent(paymentIntentId, () => {
                });
            }
        }
    };

    // Custom hook to handle applying the 'beforeunload' event listener smoothly.
    useBeforeUnload(handleLeavingPage);

    // UI ///////////////////////////////////////////////////////////////////////////
    let Body;
    if (!appCenterData || !settings) {
        Body = <></>;
    } else if (page === PAYMENT_METHOD_PAGES.SPLASH) {
        Body = (
            <PaymentMethodSplashPage data={appCenterData}
                                     setData={setAppCenterData}
                                     settings={settings}
                                     handleError={handleError}
                                     setServerResponse={setServerResponse}
                                     setLoading={setLoading}
                                     setPage={setPage}
                                     page={page}
                                     setShouldRefresh={setShouldRefresh}/>
        );
    } else if (page === PAYMENT_METHOD_PAGES.ADD_NEW && clientSecretKey) {
        Body = (
            <Elements stripe={loadStripe(publishableKey)}
                      options={{clientSecret: clientSecretKey}}>
                <PaymentMethodAddCardPage data={appCenterData}
                                          settings={settings}
                                          selectedPlan={selectedPlan}
                                          payNow={payNow}
                                          paymentIntentId={paymentIntentId}
                                          handleError={handleError}
                                          setServerResponse={setServerResponse}
                                          setShouldRefresh={setShouldRefresh}
                                          setPage={setPage}/>
            </Elements>
        );
    } else if (page === PAYMENT_METHOD_PAGES.HISTORY) {
        Body = (
            <PaymentMethodHistoryPage data={appCenterData}
                                      settings={settings}
                                      handleError={handleError}
                                      setServerResponse={setServerResponse}
                                      setLoading={setLoading}
                                      setPage={setPage}/>
        );
    }

    if (serverResponse.type !== RESPONSE_TYPES.none)
        return <ResponseCard response={serverResponse}/>;

    return (
        <div data-testid={'paymentMethodsContainer'}>
            <Loading show={loading}/>
            <PaymentMethodsDataRefreshManager
                shouldRefresh={shouldRefresh}
                setSettings={setSettings}
                setAppCenterData={setAppCenterData}
                setLoading={setLoading}
                setPaymentIntentId={setPaymentIntentId}
                setClientSecretKey={setClientSecretKey}/>
            {Body}
        </div>
    );
}

export default PaymentMethodsContainer;